import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BankAccountDetail, PortalBankAccount } from 'src/app/core/data-models/payment.data.models';
import { PaymentDataService } from 'src/app/core/data-services/payment/payment-data.service';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { LanguageService } from '../../services/language.service';
import { SharedModule } from '../../shared/shared.module';
import {
  readGeneralResponseMessage,
  readHttpErrorApiMessage,
} from 'src/app/shared/utils/api-response-message.util';

const DELETE_BANK_ACCOUNT_OTP_OPERATION_TYPE = 13;

@Component({
  selector: 'app-bank-account',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './bank-account.component.html',
  styleUrls: ['./bank-account.component.css']
})
export class BankAccountComponent implements OnInit {

  bankAccounts: PortalBankAccount[] = [];
  selectedAccount: BankAccountDetail | null = null;
  selectedAccountId = '';
  currentLang = this.languageService.currentLanguage || 'ar';
  currentRole = '';
  loading = false;
  detailLoading = false;
  deleting = false;
  settingPrimary = false;
  showIban = false;
  detailDialogVisible = false;
  otpDialogVisible = false;
  userPhone = '';
  private transitioningToOtp = false;

  readonly deleteBankAccountOtpOperationType = DELETE_BANK_ACCOUNT_OTP_OPERATION_TYPE;

  constructor(
    private paymentService: PaymentDataService,
    private router: Router,
    private route: ActivatedRoute,
    private languageService: LanguageService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.currentLang = this.languageService.currentLanguage;
    this.currentRole = this.route.snapshot.paramMap.get('role') || 'Investor';
    this.hydrateUserPhone();
    this.loadBankAccounts();
  }

  get bankName(): string {
    if (!this.selectedAccount) return '';
    return this.currentLang === 'ar'
      ? this.selectedAccount.bankNameAr || this.selectedAccount.bankNameEn
      : this.selectedAccount.bankNameEn || this.selectedAccount.bankNameAr;
  }

  get displayedIban(): string {
    if (!this.selectedAccount) return '';
    return this.showIban
      ? this.selectedAccount.fullIban || this.selectedAccount.iban
      : this.selectedAccount.iban;
  }

  getAccountBankName(account: PortalBankAccount): string {
    return this.currentLang === 'ar'
      ? account.bankNameAr || account.bankNameEn
      : account.bankNameEn || account.bankNameAr;
  }

  getListIbanDisplay(iban: string): string {
    if (!iban?.trim()) {
      return '***';
    }

    const digits = iban.replace(/\s/g, '').replace(/\D/g, '');
    if (!digits) {
      return '***';
    }

    return `****${digits.slice(-4)}`;
  }

  loadBankAccounts(): void {
    this.loading = true;
    this.paymentService.GetBankAccountsByUser().subscribe({
      next: (res) => {
        const items: PortalBankAccount[] = res?.resource?.items || [];
        this.bankAccounts = items
          .slice()
          .sort((a, b) => Number(!!b.isPrimary) - Number(!!a.isPrimary));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  addBankAccount(): void {
    this.router.navigate([`${this.currentRole}/bank-accounts/add`]);
  }

  openAccountDetail(account: PortalBankAccount): void {
    this.otpDialogVisible = false;
    this.selectedAccountId = account.id;
    this.selectedAccount = null;
    this.showIban = false;
    this.detailDialogVisible = true;
    this.detailLoading = true;

    this.paymentService.GetBankAccountById(account.id).subscribe({
      next: (res) => {
        this.selectedAccount = res?.resource ?? null;
        this.detailLoading = false;
        if (!this.selectedAccount) {
          this.notificationService.error('BankAccount.LOAD_DETAIL_FAILED');
          this.closeDetailDialog();
        }
      },
      error: (err) => {
        this.detailLoading = false;
        const apiMsg = readHttpErrorApiMessage(err);
        if (apiMsg) {
          this.notificationService.errorLiteral(apiMsg);
        } else {
          this.notificationService.error('BankAccount.LOAD_DETAIL_FAILED');
        }
        this.closeDetailDialog();
      },
    });
  }

  toggleIbanVisibility(): void {
    this.showIban = !this.showIban;
  }

  setAsPrimary(): void {
    if (!this.selectedAccountId || this.selectedAccount?.isPrimary || this.settingPrimary) {
      return;
    }

    this.settingPrimary = true;
    this.paymentService.SetAsPrimary(this.selectedAccountId).subscribe({
      next: (res) => {
        this.settingPrimary = false;
        const msg = readGeneralResponseMessage(res);
        if (msg) {
          this.notificationService.success(msg);
        } else {
          this.notificationService.success('BankAccount.SET_PRIMARY_SUCCESS');
        }
        if (this.selectedAccount) {
          this.selectedAccount = { ...this.selectedAccount, isPrimary: true };
        }
        this.loadBankAccounts();
      },
      error: (err) => {
        this.settingPrimary = false;
        const apiMsg = readHttpErrorApiMessage(err);
        if (apiMsg) {
          this.notificationService.errorLiteral(apiMsg);
        } else {
          this.notificationService.error('BankAccount.SET_PRIMARY_FAILED');
        }
      },
    });
  }

  onPrimaryToggle(checked: boolean): void {
    if (!checked || this.selectedAccount?.isPrimary) {
      return;
    }
    this.setAsPrimary();
  }

  closeDetailDialog(): void {
    if (this.deleting || this.settingPrimary) {
      return;
    }

    this.detailDialogVisible = false;

    if (this.transitioningToOtp) {
      this.transitioningToOtp = false;
      return;
    }

    if (!this.otpDialogVisible) {
      this.otpDialogVisible = false;
      this.selectedAccount = null;
      this.selectedAccountId = '';
      this.showIban = false;
    }
  }

  startDelete(): void {
    if (!this.selectedAccountId) {
      return;
    }

    if (!this.userPhone) {
      this.notificationService.error('BankAccount.OTP_SEND_FAILED');
      return;
    }

    this.transitioningToOtp = true;
    this.detailDialogVisible = false;
    this.otpDialogVisible = true;
  }

  private hydrateUserPhone(): void {
    try {
      const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
      this.userPhone = (user?.phoneNumber || '').trim();
    } catch {
      this.userPhone = '';
    }
  }

  onOtpVerified(otp: string): void {
    const digits = (otp || '').replace(/\D/g, '');
    if (digits.length !== 4 || !this.selectedAccountId || this.deleting) {
      this.notificationService.error('BankAccount.DELETE_FAILED');
      return;
    }

    this.deleting = true;
    this.paymentService.DeleteBankAccount({ id: this.selectedAccountId, otp: digits }).subscribe({
      next: (res) => {
        this.deleting = false;
        const msg = readGeneralResponseMessage(res);
        if (msg) {
          this.notificationService.success(msg);
        } else {
          this.notificationService.success('BankAccount.DELETE_SUCCESS');
        }
        this.otpDialogVisible = false;
        this.detailDialogVisible = false;
        this.selectedAccount = null;
        this.selectedAccountId = '';
        this.loadBankAccounts();
      },
      error: (err) => {
        this.deleting = false;
        const apiMsg = readHttpErrorApiMessage(err);
        if (apiMsg) {
          this.notificationService.errorLiteral(apiMsg);
        } else {
          this.notificationService.error('BankAccount.DELETE_FAILED');
        }
      },
    });
  }

  closeOtpDialog(): void {
    if (!this.deleting) {
      this.otpDialogVisible = false;
      if (this.selectedAccount) {
        this.detailDialogVisible = true;
      }
    }
  }
}
